<template>
  <div class="project-ci-cd-settings-table">
    <div class="project-ci-cd-settings-table__item">
      <div class="project-ci-cd-settings-table__item__cell__header">
        {{ $t('ci_cd.titles.key') }}
      </div>
      <div class="project-ci-cd-settings-table__item__cell__header">
        {{ $t('ci_cd.titles.value') }}
      </div>
      <div class="project-ci-cd-settings-table__item__cell__header">
        {{ $t('ci_cd.titles.environment') }}
      </div>
      <div class="project-ci-cd-settings-table__item__cell__header">
        {{ $t('ci_cd.titles.mask') }}
        <gf-custom-tooltip
          position="left"
          width="250px"
          :ignore-intersection="true"
          :show-on-mobile-version="true"
        >
          <template #element>
            <div class="d-flex align-items-center">
              <i class="gf-icon gf-icon-question-circle ml-1"></i>
            </div>
          </template>
          <template #content>
            <span>Маскирование значения в логах</span>
          </template>
        </gf-custom-tooltip>
      </div>
      <div class="project-ci-cd-settings-table__item__cell__header"></div>
    </div>

    <b class="p-2 border-right border-left">{{ $t('ci_cd.titles.newVariables') }}</b>
    <section class="project-ci-cd-settings-table__variables-to-add">
      <project-ci-cd-settings-form-table-item
        v-for="(item, index) in ciCdVariablesToAdd"
        :key="index"
        :ci-cd-variable="item"
        :errors="formErrors?.[`ciCdVariablesToAdd[${index}]`]"
        :variable-input-id-name="`ciCdVariablesToAdd${index}`"
        :variable-input-value-name="`valueToAdd${index}`"
        :variable-input-environment-name="`environmentToAdd${index}`"
        :variable-input-id-disabled="false"
        @inputId="getCiCdVariableId($event, index)"
        @inputValue="getCiCdVariableValue($event, index)"
        @inputEnvironment="getCiCdVariableEnvironment($event, index)"
        @delete="deleteNewCiCdVariable(index)"
        @maskToggle="maskToggleHandler(index, 'new')"
      />
    </section>

    <b
      v-if="this.copyOfExistingVariables?.length > 0"
      class="p-2 border-left border-right bg-light"
    >
      {{ $t('ci_cd.titles.existingVariables') }}
    </b>
    <section
      v-if="existingVariables?.length > 0"
      class="project-ci-cd-settings-table__existing-variables"
      :key="existingVariablesComponentKey"
    >
      <project-ci-cd-settings-form-table-item
        v-for="(item, index) in copyOfExistingVariables"
        :key="index"
        :ci-cd-variable="item"
        :variable-input-id-name="`keyExisting${index}`"
        :variable-input-value-name="`valueExisting${index}`"
        :variable-input-environment-name="`environmentToAdd${index}`"
        @inputValue="editExistingCiCdVariable($event, index)"
        @delete="deleteExistingCiCdVariable(index)"
        @maskToggle="maskToggleHandler(index, 'existing')"
      />
    </section>
    <div
      v-if="!isSchedule"
      class="project-ci-cd-settings-table__actions"
    >
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="resetChanges"
      >
        {{ $t('ci_cd.titles.cancelChanges') }}
      </button>

      <button
        type="button"
        class="btn btn-sm btn-success"
        :disabled="invalidVariablesFlag"
        @click="submitHandler"
      >
        {{ $t('ci_cd.titles.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import ProjectCiCdSettingsFormTableItem from '@/shared-modules/modules/project-settings/components/project-ci-cd-settings-form-table-item';
import { FORM_NAME_ENUMS } from '@/shared-modules/variables/form-name.enums';
import ProjectSettingsMessages from '@shared/modules/project-settings/project.settings.messages';
import HelperS from '@/shared-modules/services/helper.service';
import GfCustomTooltip from '@/shared-modules/shared-components/shared/GfCustomTooltip.vue';

export default {
  name: 'ProjectCiCdSettingsFormTable',
  components: { ProjectCiCdSettingsFormTableItem, GfCustomTooltip },
  props: {
    formName: {
      type: String,
      default: null
    },
    existingVariables: {
      type: Array,
      default: null
    },
    fieldsToKeep: {
      // Поля в переменных, которые необходимо оставить
      type: Array,
      default() {
        return ['key', 'value', 'environmentScope', 'isMask', 'uuid'];
      }
    },
    isSchedule: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      ciCdVariablesToAdd: [
        {
          key: '',
          value: '',
          environmentScope: null,
          isMask: false,
          canBeDeleted: false
        }
      ],
      ciCdVariablesToDelete: [],
      ciCdVariablesToEdit: [],
      copyOfExistingVariables: null,
      existingVariablesComponentKey: 0,
      copied: false
    };
  },
  computed: {
    formErrors() {
      return this.$store.getters.getFormErrors(FORM_NAME_ENUMS[this.formName]);
    },
    invalidVariablesFlag() {
      if (this.copyOfExistingVariables) {
        let invalidExistingVariablesFlag = Object.values(this.copyOfExistingVariables).some(
          (variable) => {
            return variable.key === '' || variable.value === '';
          }
        );
        return invalidExistingVariablesFlag;
      }
    }
  },
  watch: {
    existingVariables(newVal) {
      if (!this.copied) {
        this.copyOfExistingVariables = JSON.parse(JSON.stringify(newVal));
        this.copied = true;
      }
    }
  },
  methods: {
    maskToggleHandler(variableIndex, type) {
      // Mask variables value toggle handler
      switch (type) {
        case 'existing':
          this.copyOfExistingVariables[variableIndex].mask =
            !this.copyOfExistingVariables[variableIndex].mask;
          // Редактирование существующих переменных
          const localIndex = this.ciCdVariablesToEdit.findIndex(
            (item) => item.localIndex === variableIndex
          );

          if (localIndex === -1) {
            // Если переменная не найдена, добавляем ее в массив ciCdVariablesToEdit
            this.ciCdVariablesToEdit.push({
              ...this.copyOfExistingVariables[variableIndex],
              localIndex: variableIndex
            });
          } else {
            // Если переменная уже добавлена в ciCdVariablesToEdit, обновляем ее значение
            this.ciCdVariablesToEdit[localIndex]['mask'] =
              this.copyOfExistingVariables[variableIndex].mask;
          }

          break;
        case 'new':
          this.ciCdVariablesToAdd[variableIndex].isMask =
            !this.ciCdVariablesToAdd[variableIndex].isMask;
          break;
        default:
          break;
      }
    },
    getCiCdVariableId(data, variableIndex) {
      // Добавление / Редактирование ключей новых переменных
      this.ciCdVariablesToAdd[variableIndex]['key'] = data;
      if (
        variableIndex === this.ciCdVariablesToAdd?.length - 1 &&
        this.ciCdVariablesToAdd[variableIndex]['value']?.length > 0
      ) {
        this.ciCdVariablesToAdd[variableIndex]['canBeDeleted'] = true;
        this.addNewRow();
      }
    },
    getCiCdVariableValue(data, variableIndex) {
      // Изменение значений новых переменных
      if (variableIndex === this.ciCdVariablesToAdd?.length - 1 && data?.value) {
        // Пушим новый ряд в таблицу, если изменяется значение в последней новой переменной
        this.ciCdVariablesToAdd[variableIndex]['value'] = data?.value;
        if (this.ciCdVariablesToAdd[variableIndex]['key']?.length > 0) {
          this.ciCdVariablesToAdd[variableIndex]['canBeDeleted'] = true;
          this.addNewRow();
        }
      } else {
        // Иначе просто редактируем значение существующей переменной
        this.ciCdVariablesToAdd[variableIndex]['value'] = data?.value;
      }
    },
    getCiCdVariableEnvironment(data, variableIndex) {
      // Изменение значений новых переменных
      if (variableIndex === this.ciCdVariablesToAdd?.length - 1 && data) {
        this.ciCdVariablesToAdd[variableIndex]['environmentScope'] = data;
      } else {
        // Иначе просто редактируем значение существующей переменной
        this.ciCdVariablesToAdd[variableIndex]['environmentScope'] = data;
      }
    },
    addNewRow() {
      this.ciCdVariablesToAdd.push({
        key: '',
        isMask: false,
        value: '',
        environmentScope: null,
        canBeDeleted: false
      });
    },
    deleteNewCiCdVariable(variableIndex) {
      // Удаление новых переменных
      if (this.ciCdVariablesToAdd?.length > 1) {
        // Если новая переменная НЕ последняя - то удаляем переменную
        this.ciCdVariablesToAdd.splice(variableIndex, 1);
      } else {
        // Иначе, очищаем поля последней новой переменной
        this.ciCdVariablesToAdd[variableIndex]['key'] = '';
        this.ciCdVariablesToAdd[variableIndex]['value'] = '';
        this.ciCdVariablesToAdd[variableIndex]['environmentScope'] = null;
        this.ciCdVariablesToAdd[variableIndex]['mask'] = false;
      }
    },
    editExistingCiCdVariable(data, variableIndex) {
      // Редактирование существующих переменных
      const localIndex = this.ciCdVariablesToEdit.findIndex(
        (item) => item.localIndex === variableIndex
      );

      if (localIndex === -1) {
        // Если переменная не найдена, добавляем ее в массив ciCdVariablesToEdit
        this.ciCdVariablesToEdit.push({
          ...this.copyOfExistingVariables[variableIndex],
          localIndex: variableIndex
        });

        let variableToEditIndex = this.ciCdVariablesToEdit.findIndex(
          (item) => item.localIndex === variableIndex
        );
        this.ciCdVariablesToEdit[variableToEditIndex].key = data?.key;
        this.ciCdVariablesToEdit[variableToEditIndex].value = data?.value;
        this.ciCdVariablesToEdit[variableToEditIndex].environmentScope = data?.environmentScope;
      } else {
        // Если переменная уже добавлена в ciCdVariablesToEdit, обновляем ее значение
        this.ciCdVariablesToEdit[localIndex]['key'] = data?.key;
        this.ciCdVariablesToEdit[localIndex]['value'] = data?.value;
        this.ciCdVariablesToEdit[localIndex]['environmentScope'] = data?.environmentScope;
      }

      // Так же меняем значение в копии массива existingVariables, для коректного отображения на странице
      this.copyOfExistingVariables[variableIndex]['key'] = data?.key;
      this.copyOfExistingVariables[variableIndex]['value'] = data?.value;
      this.copyOfExistingVariables[variableIndex]['environmentScope'] = data?.environmentScope;
    },
    deleteExistingCiCdVariable(variableIndex) {
      // Удаление существующих переменных
      this.ciCdVariablesToDelete.push(this.copyOfExistingVariables[variableIndex]);
      const deletedVariableIndex = this.ciCdVariablesToEdit.findIndex(
        (item) => item.uuid === this.copyOfExistingVariables[variableIndex]?.uuid
      );
      this.ciCdVariablesToEdit.splice(deletedVariableIndex, 1);
      this.copyOfExistingVariables.splice(variableIndex, 1);

      for (let i = deletedVariableIndex; i < this.ciCdVariablesToEdit.length; i++) {
        this.ciCdVariablesToEdit[i].localIndex = this.ciCdVariablesToEdit[i].localIndex - 1;
      }
    },
    setOnlyNecessaryInfo(objectForFiltration) {
      const res = HelperS.filterObjectOrArrayOfObjects(objectForFiltration, this.fieldsToKeep);
      return res;
    },
    filterVariablesToEdit() {
      let resultVariablesToEdit = [];

      this.ciCdVariablesToEdit?.forEach((variable) => {
        let comparedVariable = variable;
        delete comparedVariable.localIndex;
        let existingVariable = this.existingVariables.find(
          (existingVariable) => comparedVariable.uuid === existingVariable.uuid
        );

        if (JSON.stringify(existingVariable) !== JSON.stringify(comparedVariable)) {
          resultVariablesToEdit.push(comparedVariable);
        }
      });
      return this.setOnlyNecessaryInfo(
        resultVariablesToEdit.map((variable, index) => {
          return { ...resultVariablesToEdit[index], isMask: variable.mask };
        })
      );
    },
    submitHandler() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS[this.formName],
        errors: null
      });

      this.$emit('submit', {
        ciCdVariablesToAdd: this.setOnlyNecessaryInfo(
          this.ciCdVariablesToAdd.filter(
            (item) => item.key.trim() !== '' && item.value.trim() !== ''
          )
        ),
        ciCdVariablesToEdit: this.filterVariablesToEdit(),
        ciCdVariablesToDelete: this.setOnlyNecessaryInfo(this.ciCdVariablesToDelete)
      });
    },
    resetChanges() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS[this.formName],
        errors: null
      });
      this.ciCdVariablesToEdit = [];
      this.ciCdVariablesToDelete = [];
      this.copyOfExistingVariables = JSON.parse(JSON.stringify(this.existingVariables));
      this.existingVariablesComponentKey += 1;
    }
  },
  i18n: {
    messages: ProjectSettingsMessages
  }
};
</script>

<style lang="scss">
$this: '.project-ci-cd-settings-table';

#{$this} {
  & {
    display: grid;
    grid-template-rows: auto auto;
    overflow: auto;
    &__variables-to-add {
      #{$this} {
        &__item {
          &:last-child {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }
        }
      }
    }

    &__item {
      border: 1px solid var(--border-color);
      display: grid;
      grid-template-columns: minmax(162px, 0.5fr) minmax(200px, 2fr) minmax(160px, 1fr) 80px minmax(
          54px,
          0.2fr
        );
      grid-template-rows: 2fr;
      min-width: 600px;
      gap: 10px;
      max-width: 100%;
      align-items: flex-start;

      #{$this} > &:first-child {
        border-top-left-radius: 6.4px;
        border-top-right-radius: 6.4px;
      }

      & + & {
        border-top: 0;
      }

      &:last-child {
        border-bottom-left-radius: 6.4px;
        border-bottom-right-radius: 6.4px;
      }

      & .invalid-feedback {
        grid-column: 1 / 5;
        grid-row: 2;
        margin-top: -20px;
        padding: 0 8px 8px 8px;
      }

      & .invalid-feedback + .invalid-feedback {
        margin-top: 0;
      }

      // &.has-error > * {
      //   padding-bottom: 1.5rem;
      // }

      &__cell {
        &__header {
          display: flex;
          align-items: center;
          padding: 8px;
          font-weight: 550;
          letter-spacing: 0.24px;
          align-content: center;
          border-radius: 3.2px;
        }

        &__common {
          display: flex;
          align-items: center;
          padding: 8px;
          color: var(--secondary);
          align-content: center;

          &__input-group {
            padding: 8px;
            display: flex;

            & .form-control {
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
              padding: 6px 8px;
            }

            &.--hidden:has(textarea) {
              position: relative;

              & > textarea {
                color: var(--white) !important;
              }

              &::before {
                content: '******';
                position: absolute;
                top: 18px;
                left: 16px;
                color: var(--text-muted);
                pointer-events: none;
              }
            }

            & > textarea {
              resize: none;

              &[rows='1'] {
                overflow-y: hidden;
              }
            }

            &__item {
              color: var(--text-muted);
              background-clip: padding-box;
              transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
              border: 1px solid var(--border-color);
              border-right: 0;
              border-left: 0;
              border-radius: 0;

              &.border-danger {
                border-top-right-radius: 6.4px;
                border-bottom-right-radius: 6.4px;
                border-right: 1px solid var(--danger) !important;
              }

              &:hover {
                color: var(--dark) !important;
                background-color: transparent !important;
              }

              &:focus {
                border-right: 1px solid var(--primary) !important;
                box-shadow: none !important;
              }

              &:active {
                color: var(--dark) !important;
                background-color: var(--light) !important;
              }

              &:last-child {
                border-left: 0;
                border: 1px solid var(--border-color);
                background-color: var(--bg-light);
                border-top-right-radius: 6.4px;
                border-bottom-right-radius: 6.4px;
              }

              & .input-group {
                padding-top: 1.76px;
                padding-bottom: 1.76px;
              }
            }

            &:has(&__item:last-child) {
              & .form-control-sm {
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
                border-right: 0;

                &:focus {
                  border-right: 1px solid var(--primary) !important;
                }

                &.border-danger {
                  border-right: 0 !important;
                }
              }

              & .btn-sm {
                display: flex;
                align-items: center;
                padding: 6.56px;
                border-top-right-radius: 6.4px;
                border-bottom-right-radius: 6.4px;
                border-right: 0;
              }
            }
          }
        }

        &__delete {
          display: flex;
          justify-content: end;
          padding: 8px;
          text-align: center;
          align-content: center;
        }

        &__errors {
          display: flex;
          flex-direction: column;
          padding: 8px;
          grid-column: 1 / -1;
          font-size: 12px;
          margin-top: -25px;

          & > .gf-alert {
            padding: 12px;
          }
        }
      }
    }

    &__actions {
      display: flex;
      justify-content: end;
      align-items: center;
      gap: 12px;
      padding: 8px;
    }
  }
}
</style>
